import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Layout } from '../components/Layout';
import {
  saveUserProfile,
  getUserProfile,
} from '../services/userProfileService';
import { validateProfile } from '../utils/validation';
import { CHAT_PAGE_PATH } from '@/utils/constants';
import useAuth from '../contexts/useAuth';

export const GetStartedPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const navigate = useNavigate();
  const { reloadProfile } = useAuth();

  // If user already has a profile, redirect to chat
  useEffect(() => {
    const existingProfile = getUserProfile();
    if (existingProfile) {
      navigate(CHAT_PAGE_PATH);
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setIsSubmitting(true);

    try {
      // Validate input
      const validation = validateProfile(email);

      if (!validation.isValid) {
        setErrors(validation.errors);
        setIsSubmitting(false);
        return;
      }

      // Save profile
      saveUserProfile({ email });

      // Reload profile in AuthContext before navigation
      if (reloadProfile) {
        reloadProfile();
      }

      // Small delay to ensure context updates
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Navigate to chat
      navigate(CHAT_PAGE_PATH, { replace: true });
    } catch (err: any) {
      setErrors([err.message || 'Failed to save profile. Please try again.']);
      setIsSubmitting(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom =
      Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
      5;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <Layout fullPage>
      <div className="flex flex-col bg-allianz-light items-center justify-start px-4 sm:px-6 lg:px-8 py-8 w-full pb-32">
        <div className="max-w-3xl w-full space-y-6 bg-white py-8 px-6 shadow rounded-lg h-auto flex-shrink-0">
          <div>
            {/* Focusable so keyboard-only users can scroll it. Without a
                tabindex the box is unreachable and the scroll-to-bottom gate
                below never unlocks "Accept and Continue" (WCAG 2.1.1). */}
            <div
              className="text-black text-sm space-y-3 mb-6 overflow-y-auto h-72 border-b pb-4"
              onScroll={handleScroll}
              tabIndex={0}
              role="region"
              aria-label="Terms and acknowledgements"
            >
              <h3 className="font-bold mb-2">Terms</h3>
              <p>
                Welcome to Ada, the Allianz Retire+ (AR+) Adviser Digital
                Assistant. By using Ada you are directly interacting with a
                generative AI tool and agreeing to the terms and
                acknowledgements current at the time of usage.
              </p>
              <p>
                This tool has been designed to educate advisers and paraplanners
                specifically about the Allianz Guaranteed Income for Life
                (AGILE) product which is distributed to non-super investors and
                super platform investors under the AGILE Product Disclosure
                Statement (PDS) dated 19 January 2026 (AGILE), and to individual
                direct superannuation investors under the AGILE Super+ PDS dated
                17 August 2026 (AGILE Super+). The tool is not intended for
                retail or wholesale investors and is not designed for, or
                intended for, use by clients and in particular, clients seeking
                general or personal advice. Without limitation, Allianz is not
                responsible, and assumes no duty of care, where persons who are
                not appropriately authorised financial advisers or paraplanners
                access and use this tool. The tool acts as a conversational
                chatbot that is able to comprehend curated data sources (e.g.
                the relevant AGILE or AGILE Super+ PDS) with the help of
                generative artificial intelligence (AI).
              </p>
              <p>
                As some of the features between AGILE and AGILE Super+ may vary,
                we recommend that you specifically refer to AGILE Super+ if you
                are asking Ada about that distribution channel and PDS.
                Questions that refer to AGILE may not include details about
                AGILE Super+ and vice versa. Questions that do not refer to a
                specific product may include details about both AGILE and AGILE
                Super+. All responses should be verified against the relevant
                PDS and Source Documents.
              </p>
              <p>
                No AI tool available today is completely free of hallucinations.
                This is an early version of the tool and we continue to monitor
                and refine its performance to ensure it is as accurate and
                useful as possible. It is recommended that you consult an AR+
                representative to validate any doubts or questions and we
                welcome any feedback including in relation to questions about
                any differences between AGILE and AGILE Super+.
              </p>
              <p>
                Whilst all reasonable efforts have been made to strive for
                accurate and reliable content, please note that the information
                provided may not be entirely error-free or up-to-date and there
                may be limitations, omissions or biases present. We recommend
                that you independently verify the content and implement your own
                professional expertise to consider the information or consult
                with further professionals for specific advice on financial,
                product, tax, social security or other information.
              </p>
              <p>
                AR+ will collect contact details (including email addresses)
                from users of the tool, including advisers and paraplanners for
                log-in purposes, to contact you in connection with the tool, and
                for sales team lead generation capability.{' '}
                <strong>
                  Users of the tool should not at any time enter any personal
                  information of their clients or any other person when using
                  the tool.
                </strong>{' '}
                In the event that you enter any of your client's personal
                information, contrary to these instructions, you confirm you
                have obtained your client's consent to disclose such personal
                information to us.{' '}
                <strong>
                  However this tool is not intended to provide personal advice
                  and does not take into account your client's objectives,
                  financial situation or needs. (Refer Important
                  Acknowledgements below)
                </strong>
              </p>
              <p>
                Any personal information collected via the tool is only for the
                purposes of generating content via the tool and ongoing quality
                assurance and monitoring to improve the output of the tool and
                will be handled in accordance with our{' '}
                <a
                  href="https://www.allianzretireplus.com.au/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <b>
                    <u className="underline">Privacy Policy</u>
                  </b>
                </a>
              </p>

              <h3 className="font-bold mt-4 mb-2 underline">
                Important Acknowledgements
              </h3>
              <p>
                Ada, the Allianz Retire+ Adviser Digital Assistant tool (tool)
                is for general information purposes only. It is not
                comprehensive or intended to give financial product advice. Any
                information provided by this tool is intended;
              </p>
              <ul className="list-disc ml-5 space-y-1">
                <li>to be illustrative only;</li>
                <li>
                  not to be comprehensive and in the event of any inconsistency
                  provided by this tool and the content of a Source Document,
                  the content of the Source Document prevails; and
                </li>
                <li>
                  <strong>not</strong> to be personal advice and therefore does{' '}
                  <strong>not</strong> take into account your client's
                  objectives, financial situation or needs.
                </li>
              </ul>
              <p>
                Advisers should always refer to the Source Documents to obtain
                more detailed information about AGILE and AGILE Super+.
              </p>
              <p>
                Source Documents refer to the relevant PDS and TMD, relevant law
                and related regulatory material, in each case in its current
                form at the time of the search.
              </p>
              <p>
                <strong>
                  Allianz does not authorise you to pass on the content directly
                  to clients
                </strong>
                , but rather the content is provided to assist you to provide
                your advice to your clients and{' '}
                <strong>you retain responsibility for that advice</strong>.
                Before acting on anything contained in this material, you should
                consider the appropriateness of the information received, having
                regard to your client's objectives, financial situation and
                needs. Past performance is not a reliable indicator of future
                performance.
              </p>
              <p>
                This tool should not be relied on for the purposes of making a
                financial decision or a decision in relation to a financial
                product and is only for use by licensed financial advisers and
                paraplanners. As a licensed financial adviser, you are solely
                responsible for considering the appropriateness of any product
                for your clients' circumstances. In providing the tool to you,
                Allianz Retire+ is not providing financial advice to you or your
                clients. You acknowledge that you are not acting as an agent of
                Allianz Retire+ and agree that you will not represent that you
                are an agent of Allianz Retire+.
              </p>
              <p>
                Allianz Guaranteed Income for Life (AGILE) and AGILE Super+ are
                issued by Allianz Australia Life Insurance Limited (ABN 27 076
                033 782) (AFSL 296559) (<strong>Allianz Retire+</strong>).
                Allianz Retire+ is a registered business name of Allianz
                Australia Life Insurance Limited. Prior to making an investment
                decision please read the relevant Product Disclosure Statement
                (PDS) and Target Market Determination (TMD) which are available
                on our website{' '}
                <a
                  href="https://www.allianzretireplus.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <b>
                    <u>website</u>
                  </b>
                </a>
                . Use of the word 'guarantee' in relation to AGILE and AGILE
                Super+ refers to an assurance that certain conditions or
                contractual promises will be fulfilled by Allianz Retire+ from
                the available assets of its Statutory Fund No 2, in relation to
                the product terms. This includes 'guaranteed' income payments in
                the Lifetime Income Phase which will be paid from the available
                assets of Statutory Fund No 2, noting that Allianz Retire+ may
                terminate the product in certain limited circumstances as
                outlined in the PDS available on the Allianz Retire+ website.
              </p>
              <p>
                The tool is current as of 17 August 2026 and is subject to
                change. To the extent permitted by law, neither Allianz Retire+
                nor any of its related entities, agents or employees accept any
                liability for any loss or damage arising, directly or
                indirectly, from reliance on this tool.
              </p>
              {/* underline the important acknowledgements */}
              <p>
                The tool is provided subject to the above Terms and{' '}
                <strong className="underline">
                  Important Acknowledgements
                </strong>
                .
              </p>
            </div>

            <h2 className="text-center text-3xl font-extrabold text-gray-900 mb-2">
              Welcome to Ada
            </h2>
            <p className="text-center text-sm text-gray-600 mb-6">
              Please provide your details to continue
            </p>
          </div>

          <div className="bg-white pt-0 pb-4 px-6 shadow rounded-lg h-auto">
            {errors.length > 0 && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <ul className="text-sm text-red-800 list-disc list-inside">
                      {errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email Address *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-allianz-primary focus:border-allianz-primary focus:z-10 sm:text-sm"
                  placeholder="your.email@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="text-xs text-gray-500 italic">
                <p>
                  {!hasScrolledToBottom ? (
                    <span className="text-amber-600 font-medium">
                      Please read to the end of the Terms and Important
                      Acknowledgements to continue
                    </span>
                  ) : (
                    'By clicking on the button below you agree to these Terms and Important Acknowledgements'
                  )}
                </p>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || !hasScrolledToBottom}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-allianz-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-allianz-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    'Accept and Continue'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
